<!--
  @copyright 汽车商贸管理平台 V1.0
  @year 2026
  @description ApprovalDetail 审批详情/处理（钉钉工作通知直达，后续待办列表复用）
  数据契约（后端已实现，勿改字段名）：
    GET /api/Approval/Detail?approvalNo=xxx
    POST /api/Approval/Act { approvalNo, action: approve|reject|withdraw, opinion }
-->
<template>
  <div class="page">
    <van-nav-bar title="审批详情" left-arrow @click-left="$router.back()">
      <template #title>
        <div class="nav-title">
          <div class="nav-title-main">审批详情</div>
          <div class="nav-title-sub" v-if="navSub">{{ navSub }}</div>
        </div>
      </template>
    </van-nav-bar>

    <!-- 加载中 -->
    <div v-if="loading" class="state-tip">
      <van-loading size="24" vertical>加载中...</van-loading>
    </div>

    <!-- 缺少单号 / 加载失败 -->
    <div v-else-if="!detail" class="state-tip">
      <div class="state-text">{{ loadError || "暂无审批详情" }}</div>
      <van-button
        v-if="loadError && approvalNo"
        type="primary"
        plain
        size="small"
        style="margin-top: 12px"
        @click="loadDetail"
        >重新加载</van-button
      >
    </div>

    <div v-else class="body" :class="{ 'with-actions': showActions }">
      <!-- 审批头卡 -->
      <div class="card">
        <div class="card-hd">审批信息</div>
        <div class="kv-row" v-for="(r, i) in headerCardRows" :key="i">
          <div class="kv-label">{{ r.label }}</div>
          <div class="kv-value" :class="{ 'kv-money': r.money }">
            <span
              v-if="r.status !== undefined"
              class="st-badge"
              :style="statusStyle(r.status)"
              >{{ statusText(r.status) }}</span
            >
            <template v-else>{{ r.value }}</template>
          </div>
        </div>
      </div>

      <!-- 资金审批：资金单字段 -->
      <div class="card" v-if="isFund">
        <div class="card-hd">资金信息</div>
        <template v-if="fundRows.length">
          <div class="kv-row" v-for="(r, i) in fundRows" :key="i">
            <div class="kv-label">{{ r.label }}</div>
            <div class="kv-value" :class="{ 'kv-money': r.money }">
              {{ r.value }}
            </div>
          </div>
        </template>
        <div v-else class="empty-text">暂无</div>
      </div>

      <!-- 资金审批附件（独立卡片）：库内附件 + 历史单据的 NAS 附件，标题下列文件，点击预览 -->
      <div class="card" v-if="isFund && fundAttachments.length">
        <div class="card-hd">附件（{{ fundAttachments.length }}）</div>
        <div class="kv-row" v-for="(a, i) in fundAttachments" :key="'fa' + i">
          <div class="kv-label">{{ i + 1 }}</div>
          <div class="kv-value">
            <span class="file-link" @click="openFundAttachment(a.name)">{{ a.name }}</span>
            <span v-if="a.size" class="file-size">{{ sizeText(a.size) }}</span>
            <span v-else class="file-size">（历史附件）</span>
          </div>        </div>
      </div>

      <!-- 业务型审批 -->
      <template v-if="!isFund && detail.business">
        <!-- 订单信息（orderInfo + 供货/需求方） -->
        <div class="card" v-if="orderRows.length">
          <div class="card-hd">订单信息</div>
          <div class="kv-row" v-for="(r, i) in orderRows" :key="i">
            <div class="kv-label">{{ r.label }}</div>
            <div class="kv-value" :class="{ 'kv-money': r.money }">
              {{ r.value }}
            </div>
          </div>
        </div>

        <!-- 合同列表（数量多可展开） -->
        <div class="card" v-if="contracts.length">
          <div class="card-hd">合同信息（{{ contracts.length }}）</div>
          <div class="sub-item" v-for="(c, ci) in visibleContracts" :key="ci">
            <div class="sub-title">合同 {{ ci + 1 }}</div>
            <div class="kv-row" v-for="(r, ri) in contractRow(c)" :key="ri">
              <div class="kv-label">{{ r.label }}</div>
              <div class="kv-value" :class="{ 'kv-money': r.money }">
                {{ r.value }}
              </div>
            </div>
          </div>
          <div
            v-if="contracts.length > shownContractLimit"
            class="toggle-btn"
            @click="contractsExpanded = !contractsExpanded"
          >
            {{ contractsExpanded ? "收起" : "展开全部（共 " + contracts.length + " 份）" }}
          </div>
        </div>

        <!-- 明细表格（details 或 form 中 JSON 型字段解析） -->
        <div class="card" v-if="detail.business">
          <div class="card-hd">{{ detailsName || "业务明细" }}</div>
          <template v-if="detailsData && detailsData.length">
            <div class="dyn-wrap">
              <table class="dyn-table">
                <thead>
                  <tr>
                    <th v-for="(k, ki) in detailsColumns" :key="ki">{{ k }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, ri) in visibleDetails" :key="ri">
                    <td v-for="(k, kj) in detailsColumns" :key="kj">
                      {{ cellText(row, k) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              v-if="detailsData.length > shownDetailLimit"
              class="toggle-btn"
              @click="detailsExpanded = !detailsExpanded"
            >
              {{ detailsExpanded ? "收起" : "展开全部（共 " + detailsData.length + " 行）" }}
            </div>
          </template>
          <div v-else class="empty-text">暂无</div>
        </div>

        <!-- form 简单字段（用章名称/用印材料/监管仓地点 等；已在上方展示的字段不再重复） -->
        <div class="card" v-if="formRows.length">
          <div class="card-hd">单据附加信息</div>
          <div class="kv-row" v-for="(r, i) in formRows" :key="i">
            <div class="kv-label">{{ r.label }}</div>
            <div class="kv-value" :class="{ 'kv-money': r.money }">
              <template v-if="r.files && r.files.length">
                <span
                  class="file-link"
                  v-for="(f, fi) in r.files"
                  :key="fi"
                  @click="openAttachment(r.fileType, f)"
                  >{{ f }}</span
                >
              </template>
              <template v-else>{{ r.value }}</template>
            </div>
          </div>
        </div>

        <!-- 比价信息（图片可点开查看） -->
        <div class="card" v-if="compareInfo">
          <div class="card-hd">比价信息</div>
          <div class="kv-row">
            <div class="kv-label">比价数量</div>
            <div class="kv-value">{{ compareInfo.qty }}</div>
          </div>
          <div class="kv-row">
            <div class="kv-label">价格合理</div>
            <div class="kv-value">{{ compareInfo.reasonable }}</div>
          </div>
          <div class="kv-row">
            <div class="kv-label">比价图片</div>
            <div class="kv-value">
              <template v-if="compareInfo.images.length">
                <span
                  class="file-link"
                  v-for="(f, fi) in compareInfo.images"
                  :key="fi"
                  @click="openAttachment('比价图片', f)"
                  >{{ f }}</span
                >
              </template>
              <template v-else>-</template>
            </div>
          </div>
        </div>
      </template>

      <!-- 配置驱动区块（审批表单字段配置）：新增审批模板只配数据即可，前端无需改动 -->
      <div class="card" v-for="(sec, si) in sections" :key="'sec' + si">
        <div class="card-hd">{{ sec.title || "审批表单" }}</div>
        <div class="kv-row" v-for="(f, fi) in sec.fields" :key="fi">
          <div class="kv-label">{{ f.label }}</div>
          <div class="kv-value" :class="{ 'kv-money': f.type === 'money' }">
            <template v-if="f.type === 'files' && fileNames(f.value).length">
              <span
                class="file-link"
                v-for="(fn, ni) in fileNames(f.value)"
                :key="ni"
                @click="openAttachment(f.fileType, fn)"
                >{{ fn }}</span
              >
            </template>
            <template v-else-if="f.type === 'money'">{{ moneyText(f.value) }}</template>
            <template v-else-if="f.type === 'date'">{{ timeText(f.value) }}</template>
            <template v-else-if="f.type === 'multiline'">
              <span class="pre-wrap">{{ display(f.value) }}</span>
            </template>
            <template v-else>{{ display(f.value) }}</template>
          </div>
        </div>
      </div>

      <!-- 通用兜底：新审批模板（后端未做专属内容区）按 显示名/值 直接展示，保证不白屏 -->
      <div class="card" v-if="genericRows.length">
        <div class="card-hd">审批表单</div>
        <div class="kv-row" v-for="(r, i) in genericRows" :key="i">
          <div class="kv-label">{{ r.label }}</div>
          <div class="kv-value" :class="{ 'kv-money': r.money }">
            {{ r.money ? moneyText(r.value) : display(r.value) }}
          </div>
        </div>
      </div>

      <!-- 审批节点时间线 -->
      <div class="card">
        <div class="card-hd">审批进度</div>
        <template v-if="nodes.length">
          <div class="tl-item" v-for="(n, i) in nodes" :key="i">
            <span
              class="tl-dot"
              :style="{ background: nodeDotColor(n) }"
            ></span>
            <div class="tl-body">
              <div class="tl-head">
                <span class="tl-name">{{ n.name || "-" }}</span>
                <span v-if="n.isCurrent" class="tag-cur">当前</span>
                <span
                  v-if="n.status"
                  class="st-badge"
                  :style="statusStyle(n.status)"
                  >{{ statusText(n.status) }}</span
                >
              </div>
              <div class="tl-meta">
                <span class="tl-approver">审批人：{{ n.approver || "-" }}</span>
                <span class="tl-time">{{ timeText(n.time) }}</span>
              </div>
              <div class="tl-opinion" v-if="n.opinion">
                意见：{{ n.opinion }}
              </div>
            </div>
          </div>
        </template>
        <div v-else class="empty-text">暂无审批记录</div>
      </div>
    </div>

    <!-- 底部操作栏（按 actions 显隐）：审批意见 + 通过/驳回/撤销 -->
    <div class="action-bar" v-if="showActions">
      <van-field
        v-if="showOpinionInput"
        v-model="opinion"
        class="opinion-field"
        :class="{ 'field-error': opinionRequired }"
        type="textarea"
        rows="1"
        autosize
        maxlength="200"
        placeholder="审批意见（驳回必填，通过可留空）"
      />
      <div class="action-btns">
        <van-button
          v-if="acts.canApprove"
          type="primary"
          round
          :loading="acting === 'approve'"
          :disabled="!!acting"
          @click="doApprove"
          >通过</van-button
        >
        <van-button
          v-if="acts.canReject"
          type="danger"
          round
          :loading="acting === 'reject'"
          :disabled="!!acting"
          @click="doReject"
          >驳回</van-button
        >
        <van-button
          v-if="acts.canWithdraw"
          plain
          type="info"
          round
          :loading="acting === 'withdraw'"
          :disabled="!!acting"
          @click="confirmWithdraw"
          >撤销</van-button
        >
      </div>
    </div>
  </div>
</template>

<script>
import {
  approvalDetail,
  approvalAct,
  getApprovalAttachmentBlob,
  getFundAttachmentBlob,
} from "../api/index";
import { Toast, Dialog, ImagePreview } from "vant";

const NAME_KEYS = [
  "企业名称",
  "单位名称",
  "公司名称",
  "供货单位",
  "需求单位",
  "名称",
  "姓名",
  "单位",
];

// 上方卡片已展示的字段，不在"单据附加信息"重复展示
const HIDDEN_FORM_KEYS = [
  "序号",
  "审批单号",
  "业务类型",
  "业务模式",
  "供货方",
  "需求方",
  "关联先采待销审批单",
  "合同销售金额",
  "退税金额",
];

// 附件类字段（值=文件名，多个用；分隔）：可点开查看
const FILE_KEYS = ["合同文件", "附件"];
const FILE_TYPE_MAP = { 合同文件: "合同文件", 附件: "附件" };

export default {
  name: "ApprovalDetail",
  data() {
    return {
      approvalNo: "",
      detail: null,
      loading: false,
      loadError: "",
      opinion: "",
      opinionRequired: false,
      acting: "",
      contractsExpanded: false,
      detailsExpanded: false,
      previewUrls: [],
    };
  },
  computed: {
    navSub() {
      const h = this.detail && this.detail.header;
      if (!h) return this.loading ? "加载中..." : "";
      const arr = [(h.type || "").trim(), (h.approvalNo || "").trim()].filter(
        Boolean
      );
      return arr.join(" · ");
    },
    isFund() {
      return !!(this.detail && this.detail.isFund);
    },
    acts() {
      return (this.detail && this.detail.actions) || {};
    },
    showActions() {
      return !!(this.acts.canApprove || this.acts.canReject || this.acts.canWithdraw);
    },
    // 需要填写审批意见时（通过/驳回）才显示输入框；仅撤销时不需要
    showOpinionInput() {
      return !!(this.acts.canApprove || this.acts.canReject);
    },
    headerCardRows() {
      const h = (this.detail && this.detail.header) || {};
      return [
        { label: "审批类型", value: this.display(h.type) },
        { label: "审批单号", value: this.display(h.approvalNo) },
        { label: "关联订单", value: this.display(h.orderNo) },
        { label: "申请人", value: this.display(h.applicant) },
        { label: "申请时间", value: this.timeText(h.applyTime) },
        { label: "申请金额", value: this.moneyText(h.amount), money: true },
        { label: "当前状态", status: h.status },
        { label: "备注", value: this.display(h.remark) },
      ];
    },
    fundRows() {
      const f = this.detail && this.detail.fund;
      if (!f || typeof f !== "object") return [];
      // 注：附件不在此卡片展示，统一走下方独立的「附件」卡片
      const defs = [
        ["付款事项"],
        ["付款单位"],
        ["收款单位"],
        ["支付金额", "money"],
        ["详细说明"],
        ["付款方式"],
        ["开户行"],
        ["银行账号"],
        ["资金用途"],
        ["其他"],
      ];
      return defs.map(([key, type]) => ({
        label: key,
        value: type === "money" ? this.moneyText(f[key]) : this.display(f[key]),
        money: type === "money",
      }));
    },
    // 资金审批的库内附件（审批附件表，文件类型=资金附件）
    fundDbAttachments() {
      const list = (this.detail && this.detail.attachments) || [];
      if (!Array.isArray(list)) return [];
      return list.filter(
        (a) => String((a && a["文件类型"]) || "").trim() === "资金附件"
      );
    },
    // 资金审批附件：库内附件（审批附件.资金附件）优先；
    // 仅当库内没有附件时（历史单据只存了 NAS）才回退到 资金审批单v2.附件 解析出的文件名，
    // 避免同一文件因"库内=原始名 / NAS=GUID前缀名"而被重复列出。
    fundAttachments() {
      const db = this.fundDbAttachments || [];
      if (db.length) {
        return db
          .map((a) => ({
            name: String(a["文件名"] || "").trim(),
            size: a["文件大小"] || 0,
            fromDb: true,
          }))
          .filter((x) => x.name);
      }
      const f = (this.detail && this.detail.fund) || {};
      const nas = Array.isArray(f["附件文件"]) ? f["附件文件"] : [];
      return nas
        .map((n) => ({ name: String(n || "").trim(), size: 0, fromDb: false }))
        .filter((x) => x.name);
    },
    orderRows() {
      const biz = this.detail && this.detail.business;
      if (!biz) return [];
      const oi = (biz.orderInfo && typeof biz.orderInfo === "object") ? biz.orderInfo : {};
      return [
        { label: "业务类型", value: this.display(oi["业务类型"]) },
        { label: "销售模式", value: this.display(oi["销售模式"]) },
        {
          label: "合同人民币金额",
          value: this.moneyText(oi["合同人民币金额"]),
          money: true,
        },
        { label: "退税金额", value: this.moneyText(oi["退税金额"]), money: true },
        { label: "供货方", value: this.display(this.humanText(biz.supplier)) },
        { label: "需求方", value: this.display(this.humanText(biz.demand)) },
      ];
    },
    business() {
      return (this.detail && this.detail.business) || null;
    },
    contracts() {
      const c = this.business && this.business.contracts;
      return Array.isArray(c) ? c : [];
    },
    shownContractLimit() {
      return this.contractsExpanded ? this.contracts.length : 3;
    },
    visibleContracts() {
      return this.contracts.slice(0, this.shownContractLimit);
    },
    // details（后端直接给出）→ form 中 JSON 型字段 → 空
    detailsInfo() {
      const biz = this.business;
      if (!biz) return { rows: null, name: "", key: "" };
      if (Array.isArray(biz.details) && biz.details.length) {
        return { rows: biz.details, name: "业务明细", key: "" };
      }
      const form = biz.form;
      if (form && typeof form === "object") {
        const known = ["汽车贸易明细", "商品贸易明细", "比价信息"];
        for (const key of known) {
          const rows = this.parseDetailArr(form[key]);
          if (rows) return { rows, name: key, key };
        }
        for (const key of Object.keys(form)) {
          const rows = this.parseDetailArr(form[key]);
          if (rows) return { rows, name: key, key };
        }
      }
      return { rows: null, name: "", key: "" };
    },
    detailsData() {
      return this.detailsInfo.rows;
    },
    detailsName() {
      return this.detailsInfo.name;
    },
    detailsColumns() {
      const rows = this.detailsData || [];
      const keys = [];
      rows.forEach((r) => {
        if (r && typeof r === "object") {
          Object.keys(r).forEach((k) => {
            if (keys.indexOf(k) === -1) keys.push(k);
          });
        }
      });
      return keys;
    },
    shownDetailLimit() {
      return this.detailsExpanded ? (this.detailsData || []).length : 5;
    },
    visibleDetails() {
      return (this.detailsData || []).slice(0, this.shownDetailLimit);
    },
    formRows() {
      const biz = this.business;
      const form = biz && biz.form;
      if (!form || typeof form !== "object") return [];
      const consumed = this.detailsInfo.key;
      const rows = [];
      Object.keys(form).forEach((key) => {
        if (consumed && key === consumed) return;
        // 上方卡片已展示的字段不再重复（序号/审批单号/业务类型/业务模式/供货方/需求方/
        // 关联先采待销审批单/合同销售金额/退税金额）
        if (HIDDEN_FORM_KEYS.indexOf(key) !== -1) return;
        const v = form[key];
        if (v === null || v === undefined) return;
        if (typeof v === "object") {
          if (Array.isArray(v) && v.length && typeof v[0] !== "object") {
            // 纯字符串数组（如若干印章名称）→ 顿号连接展示
            rows.push({
              label: key,
              value: this.display(v.join("、")),
              money: false,
            });
          }
          return;
        }
        if (typeof v === "string" && /^[\[{]/.test(v.trim())) {
          return; // JSON 数组/对象字符串（贸易明细/比价信息等），由专门卡片展示
        }
        // 合同文件 / 附件：文件名列表 → 可点开查看
        if (FILE_KEYS.indexOf(key) !== -1) {
          const files = String(v)
            .split(/[；;]/)
            .map((s) => s.trim())
            .filter(Boolean);
          if (files.length === 0) return;
          rows.push({
            label: key,
            value: this.display(v),
            money: false,
            files,
            fileType: FILE_TYPE_MAP[key] || key,
          });
          return;
        }
        // 纯逗号/空值（历史脏数据 ",,"）→ 显示 -
        const money = this.isMoneyKey(key) && !isNaN(Number(v));
        rows.push({
          label: key,
          value: money ? this.moneyText(v) : this.display(v),
          money,
        });
      });
      return rows;
    },
    /* 比价信息：{"图片":[...],"数量":1.0,"价格合理":"是"} */
    compareInfo() {
      const form = this.business && this.business.form;
      if (!form || typeof form !== "object") return null;
      const raw = form["比价信息"];
      if (!raw || typeof raw !== "string") return null;
      const t = raw.trim();
      if (!/^\{/.test(t)) return null;
      let obj = null;
      try {
        obj = JSON.parse(t);
      } catch (e) {
        return null;
      }
      if (!obj || typeof obj !== "object") return null;
      const images = Array.isArray(obj["图片"])
        ? obj["图片"].map((x) => String(x)).filter(Boolean)
        : [];
      return {
        images,
        qty: this.display(obj["数量"]),
        reasonable: this.display(obj["价格合理"]),
      };
    },
    nodes() {
      const n = this.detail && this.detail.nodes;
      return Array.isArray(n) ? n : [];
    },
    // 配置驱动分组（后端按 审批表单字段配置 输出）：新增模板无需改前端
    sections() {
      const s = this.detail && this.detail.sections;
      if (!Array.isArray(s)) return [];
      return s.filter((x) => x && Array.isArray(x.fields) && x.fields.length);
    },
    // 新审批模板兜底字段（后端 formType=generic 时返回）：新模板无需改前端即可展示
    genericRows() {
      const g = this.detail && this.detail.genericFields;
      if (!Array.isArray(g)) return [];
      return g
        .filter((x) => x && x.label !== undefined && x.value !== undefined && x.value !== null)
        .map((x) => ({
          label: String(x.label),
          value: x.value,
          money: !!x.money,
        }));
    },
  },
  created() {
    this.approvalNo = (this.$route.query && this.$route.query.no) || "";
    if (this.approvalNo) {
      this.loadDetail();
    } else {
      this.loadError = "缺少审批单号，无法加载";
    }
  },
  beforeDestroy() {
    this.releasePreviewUrls();
  },
  watch: {
    "$route.query.no"(v) {
      if (v && v !== this.approvalNo) {
        this.approvalNo = v;
        this.loadDetail();
      }
    },
    // 一旦开始输入，取消"必填"标红提示
    opinion(v) {
      if (this.opinionRequired && (v || "").trim()) this.opinionRequired = false;
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    async loadDetail() {
      if (!this.approvalNo) return;
      this.loading = true;
      this.loadError = "";
      try {
        const res = await approvalDetail(this.approvalNo);
        if (res && res.code === 0 && res.data) {
          this.detail = res.data;
          // 状态变化后重置展开态
          this.contractsExpanded = false;
          this.detailsExpanded = false;
        } else {
          this.detail = null;
          this.loadError = (res && res.message) || "审批详情加载失败";
          if (this.loadError) Toast.fail(this.loadError);
        }
      } catch (e) {
        console.error("加载审批详情失败:", e);
        this.detail = null;
        this.loadError = "审批详情加载失败";
      } finally {
        this.loading = false;
      }
    },

    /* ---------- 展示辅助 ---------- */
    display(v) {
      if (v === null || v === undefined) return "-";
      const s = String(v).trim();
      return s === "" ? "-" : s;
    },
    timeText(v) {
      if (v === null || v === undefined) return "-";
      const s = String(v).trim();
      if (!s) return "-";
      // 兼容毫秒/秒时间戳
      if (/^\d{10,13}$/.test(s)) {
        const d = new Date(s.length === 10 ? Number(s) * 1000 : Number(s));
        if (!isNaN(d.getTime())) {
          const pad = (x) => (x < 10 ? "0" + x : "" + x);
          return (
            d.getFullYear() +
            "-" +
            pad(d.getMonth() + 1) +
            "-" +
            pad(d.getDate()) +
            " " +
            pad(d.getHours()) +
            ":" +
            pad(d.getMinutes())
          );
        }
      }
      return s.replace("T", " ").replace(/\.\d+$/, "").trim() || "-";
    },
    moneyText(v) {
      if (v === null || v === undefined) return "-";
      const s = String(v).trim();
      if (!s) return "-";
      const n = Number(v);
      return isNaN(n) ? s : n.toFixed(2);
    },
    humanText(v) {
      if (v === null || v === undefined) return "";
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        return String(v);
      }
      if (Array.isArray(v)) {
        return v
          .map((x) => this.humanText(x))
          .filter(Boolean)
          .join("、");
      }
      if (typeof v === "object") {
        for (const k of NAME_KEYS) {
          if (v[k] !== null && v[k] !== undefined && String(v[k]).trim()) {
            return String(v[k]);
          }
        }
        const parts = Object.keys(v)
          .filter((k) => typeof v[k] === "string" || typeof v[k] === "number")
          .map((k) => k + "：" + v[k]);
        if (parts.length) return parts.join("；");
        return JSON.stringify(v);
      }
      return String(v);
    },
    statusMeta(s) {
      const raw = s === null || s === undefined ? "" : String(s).trim();
      // 展示名统一：已完成 → 审批通过（与钉钉卡片状态条一致）
      const text = raw === "已完成" ? "审批通过" : raw;
      if (!text) return { text: "-", color: "#969799", bg: "#f2f3f5" };
      if (/通过|同意|完成|结束|归档|已通过/.test(text)) {
        return { text, color: "#07c160", bg: "#e8f9ef" };
      }
      if (/驳回|拒绝|不同意|退回/.test(text)) {
        return { text, color: "#ee0a24", bg: "#ffeded" };
      }
      if (/撤销|撤回|取消|作废|终止/.test(text)) {
        return { text, color: "#969799", bg: "#f2f3f5" };
      }
      if (/审批中|处理中|审核中|待审批|待审核|待处理|进行中/.test(text)) {
        return { text, color: "#1989fa", bg: "#ecf5ff" };
      }
      return { text, color: "#969799", bg: "#f2f3f5" };
    },
    statusText(s) {
      return this.statusMeta(s).text;
    },
    statusStyle(s) {
      const m = this.statusMeta(s);
      return `color:${m.color};background:${m.bg};`;
    },
    nodeDotColor(n) {
      if (n && n.isCurrent) return "#1989fa";
      const s = n && n.status;
      const t = s ? String(s) : "";
      if (/通过|同意|完成/.test(t)) return "#07c160";
      if (/驳回|拒绝|不同意|退回/.test(t)) return "#ee0a24";
      if (/撤销|撤回|取消/.test(t)) return "#969799";
      return "#c8c9cc";
    },
    parseDetailArr(v) {
      if (Array.isArray(v) && v.length && typeof v[0] === "object" && !Array.isArray(v[0])) {
        return v;
      }
      if (typeof v === "string") {
        const t = v.trim();
        if (/^\[/.test(t)) {
          try {
            const p = JSON.parse(t);
            if (
              Array.isArray(p) &&
              p.length &&
              typeof p[0] === "object" &&
              !Array.isArray(p[0])
            ) {
              return p;
            }
          } catch (e) {
            /* 解析失败则忽略 */
          }
        }
      }
      return null;
    },
    contractRow(c) {
      if (!c || typeof c !== "object") return [];
      const pick = (cands) => {
        for (const k of cands) {
          const v = c[k];
          if (v !== null && v !== undefined && String(v).trim() !== "") return v;
        }
        return "";
      };
      const moneyVal = (v) => (this.moneyText(v) === "-" ? "-" : this.moneyText(v));
      return [
        { label: "类型", value: this.display(pick(["类型", "合同类型", "合同类别", "category"])) },
        {
          label: "合同号",
          value: this.display(pick(["合同号", "合同编号", "编号", "单号", "contractNo"])),
        },
        {
          label: "对方",
          value: this.display(
            this.humanText(
              pick(["对方", "对方单位", "对方名称", "相对方", "签约对方", "单位", "公司"])
            )
          ),
        },
        {
          label: "金额",
          value: moneyVal(pick(["金额", "合同金额", "总金额", "合同人民币金额", "amount"])),
          money: true,
        },
        {
          label: "日期",
          value: this.display(pick(["日期", "签订日期", "签署日期", "签约日期", "合同日期", "date"])),
        },
      ];
    },
    isMoneyKey(k) {
      return /金额|合计|成本|利润|单价|货款|总价|税/.test(String(k || ""));
    },
    cellText(row, key) {
      if (!row || typeof row !== "object") return "-";
      const v = row[key];
      if (v === null || v === undefined || String(v).trim() === "") return "-";
      if (this.isMoneyKey(key) && !isNaN(Number(v))) return Number(v).toFixed(2);
      return String(v);
    },
    sizeText(v) {
      if (v === null || v === undefined || String(v).trim() === "") return "";
      const n = Number(v);
      if (isNaN(n)) return String(v);
      if (n < 1024) return n + "B";
      if (n < 1024 * 1024) return (n / 1024).toFixed(1) + "KB";
      return (n / 1024 / 1024).toFixed(2) + "MB";
    },

    /* ---------- 附件点开查看 ---------- */
    // 文件名串（多个用 ；/; 分隔）→ 数组
    fileNames(v) {
      if (v === null || v === undefined) return [];
      return String(v)
        .split(/[；;]/)
        .map((s) => s.trim())
        .filter(Boolean);
    },
    // 按 文件类型+文件名 找附件序号（详情接口的 attachments 清单）
    findAttachmentSeq(fileType, fileName) {
      const list = (this.detail && this.detail.attachments) || [];
      const t = String(fileType || "").trim();
      const n = String(fileName || "").trim();
      for (const a of list) {
        if (String(a["文件类型"] || "").trim() !== t) continue;
        if (String(a["文件名"] || "").trim() === n) return a["序号"];
      }
      return 0;
    },
    // 点开资金审批附件：库内（审批附件.序号）优先，历史单据回退 NAS 接口
    async openFundAttachment(fileName) {
      const hit = (this.fundDbAttachments || []).find(
        (a) => String(a["文件名"] || "").trim() === String(fileName || "").trim()
      );
      if (hit && hit["序号"]) {
        await this.openAttachment(hit["文件类型"] || "资金附件", fileName);
        return;
      }
      Toast.loading({ message: "加载中...", forbidClick: true, duration: 0 });
      try {
        const blob = await getFundAttachmentBlob(this.approvalNo, fileName);
        Toast.clear();
        const type = (blob && blob.type) || "";
        if (type.indexOf("image/") === 0) {
          const url = URL.createObjectURL(blob);
          this.previewUrls.push(url);
          ImagePreview({ images: [url], startPosition: 0, closeable: true });
        } else {
          const url = URL.createObjectURL(blob);
          this.previewUrls.push(url);
          const win = window.open(url, "_blank");
          if (!win) Toast("浏览器阻止了新窗口，请允许弹窗后重试");
        }
      } catch (e) {
        Toast.clear();
        Toast.fail("附件加载失败（可能 NAS 文件已删除）");
        console.error("资金审批附件加载失败:", e);
      }
    },
    // 点开附件：图片直接预览，其他类型下载/新窗口打开
    async openAttachment(fileType, fileName) {
      const seq = this.findAttachmentSeq(fileType, fileName);
      if (!seq) {
        Toast.fail("未找到该附件文件");
        return;
      }
      Toast.loading({ message: "加载中...", forbidClick: true, duration: 0 });
      try {
        const blob = await getApprovalAttachmentBlob(seq);
        Toast.clear();
        const type = (blob && blob.type) || "";
        if (type.indexOf("image/") === 0) {
          const url = URL.createObjectURL(blob);
          this.previewUrls.push(url);
          const images = [url];
          // 同类型其他图片可一起左右滑动预览
          const list = ((this.detail && this.detail.attachments) || []).filter(
            (a) => String(a["文件类型"] || "").trim() === String(fileType || "").trim()
          );
          for (const a of list) {
            const nm = String(a["文件名"] || "").trim();
            if (nm === String(fileName || "").trim()) continue;
            if (!/\.(png|jpe?g|gif|webp|bmp)$/i.test(nm)) continue;
            try {
              const b2 = await getApprovalAttachmentBlob(a["序号"]);
              if (b2 && (b2.type || "").indexOf("image/") === 0) {
                const u2 = URL.createObjectURL(b2);
                this.previewUrls.push(u2);
                images.push(u2);
              }
            } catch (e) {
              /* 单个失败忽略 */
            }
          }
          ImagePreview({ images, startPosition: 0, closeable: true });
        } else {
          // 非图片：新窗口打开（浏览器可预览 PDF/txt，其他类型触发下载）
          const url = URL.createObjectURL(blob);
          this.previewUrls.push(url);
          const win = window.open(url, "_blank");
          if (!win) Toast("浏览器阻止了新窗口，请允许弹窗后重试");
        }
      } catch (e) {
        Toast.clear();
        Toast.fail("附件加载失败");
        console.error("附件加载失败:", e);
      }
    },
    releasePreviewUrls() {
      (this.previewUrls || []).forEach((u) => URL.revokeObjectURL(u));
      this.previewUrls = [];
    },

    /* ---------- 审批操作 ---------- */
    // 通过：意见选填（留空由后端默认记"审核通过"）
    doApprove() {
      this.submitAct("approve", (this.opinion || "").trim());
    },
    // 驳回：审批意见必填（与后端双重校验），二次确认后再提交
    doReject() {
      const op = (this.opinion || "").trim();
      if (!op) {
        this.opinionRequired = true; // 输入框标红提示
        Toast("驳回必须填写审批意见");
        return;
      }
      Dialog.confirm({
        title: "驳回审批",
        message: "确认驳回该审批？驳回后将回退到上一节点。",
        confirmButtonColor: "#ee0a24",
      })
        .then(() => this.submitAct("reject", op))
        .catch(() => {
          /* 用户取消 */
        });
    },
    confirmWithdraw() {
      Dialog.confirm({
        title: "撤销申请",
        message: "确定要撤销该审批申请吗？撤销后不可恢复。",
        confirmButtonColor: "#1989fa",
      })
        .then(() => this.submitAct("withdraw", ""))
        .catch(() => {
          /* 用户取消 */
        });
    },
    async submitAct(action, opinion) {
      if (this.acting) return false;
      this.acting = action;
      try {
        const res = await approvalAct({
          approvalNo: this.approvalNo,
          action: action,
          opinion: opinion || "",
        });
        if (res && res.code === 0) {
          Toast.success((res.message && String(res.message)) || "操作成功");
          this.opinion = "";
          this.opinionRequired = false;
          await this.loadDetail(); // 状态变化/按钮显隐刷新
          return true;
        }
        // 后端校验未通过（如驳回未填意见）时也标红提示
        if (action === "reject" && res && String(res.message || "").indexOf("审批意见") >= 0) {
          this.opinionRequired = true;
        }
        Toast.fail((res && res.message) || "操作失败");
        return false;
      } catch (e) {
        console.error("提交审批操作失败:", e);
        return false;
      } finally {
        this.acting = "";
      }
    },
  },
};
</script>

<style scoped>
.page {
  max-width: 512px;
  margin: 0 auto;
  min-height: 100vh;
  background-color: #f5f5f5;
  padding-bottom: 24px;
}

/* 顶部导航标题：主标题 + 类型/单号副标题
   /deep/ 兜底：避免 Vant 自带 van-ellipsis(nowrap/overflow) 把副标题裁掉 */
.nav-title {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.2;
  overflow: hidden;
}
.nav-title-main {
  font-size: 16px;
  font-weight: 600;
  color: #323233;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nav-title-sub {
  font-size: 10px;
  color: #969799;
  margin-top: 1px;
  max-width: 260px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.page /deep/ .van-nav-bar__title {
  white-space: normal;
  overflow: visible;
  line-height: 1.2;
}

.body {
  padding: 10px 12px 12px;
}
.body.with-actions {
  padding-bottom: 160px; /* 给两行底部操作栏（意见框+按钮）留空间 */
}

.state-tip {
  text-align: center;
  color: #999;
  padding: 60px 20px;
  font-size: 14px;
}
.state-text {
  color: #999;
}

.card {
  background: #fff;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
}
.card-hd {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid #f2f3f5;
  display: flex;
  align-items: center;
}
.card-hd::before {
  content: "";
  width: 3px;
  height: 14px;
  border-radius: 2px;
  background: #1989fa;
  margin-right: 6px;
}

.kv-row {
  display: flex;
  align-items: flex-start;
  padding: 6px 0;
  font-size: 13px;
}
.kv-label {
  width: 84px;
  flex-shrink: 0;
  color: #969799;
  line-height: 1.5;
}
.kv-value {
  flex: 1;
  min-width: 0;
  color: #323233;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.kv-money {
  font-family: Arial, Helvetica, sans-serif;
}

/* 状态徽标 */
.st-badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 4px 10px;
  border-radius: 12px;
  white-space: nowrap;
}

.empty-text {
  color: #999;
  font-size: 13px;
  padding: 8px 0 4px;
  text-align: center;
}

/* 子列表（合同等） */
.sub-item {
  border: 1px solid #f2f3f5;
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 8px;
}
.sub-item:last-child {
  margin-bottom: 0;
}
.sub-title {
  font-size: 13px;
  font-weight: 600;
  color: #576b95;
  padding-bottom: 2px;
}
.toggle-btn {
  text-align: center;
  color: #1989fa;
  font-size: 13px;
  padding: 8px 0 2px;
}

/* 明细动态表格（横向可滚动） */
.dyn-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.dyn-table {
  border-collapse: collapse;
  font-size: 12px;
  width: 100%;
}
.dyn-table th,
.dyn-table td {
  border: 1px solid #ebeef5;
  padding: 5px 6px;
  text-align: left;
  white-space: nowrap;
  color: #323233;
  min-width: 60px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.dyn-table th {
  background: #f7f8fa;
  color: #969799;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
}
.dyn-table td {
  white-space: pre-wrap;
  word-break: break-all;
}

/* 审批时间线 */
.tl-item {
  position: relative;
  padding-left: 22px;
  padding-bottom: 18px;
}
.tl-item:last-child {
  padding-bottom: 2px;
}
.tl-item::before {
  content: "";
  position: absolute;
  left: 7px;
  top: 16px;
  bottom: 0;
  width: 2px;
  background: #e9eaee;
}
.tl-item:last-child::before {
  display: none;
}
.tl-dot {
  position: absolute;
  left: 3px;
  top: 5px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  z-index: 1;
}
.tl-body {
  min-height: 22px;
}
.tl-head {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}
.tl-name {
  font-size: 14px;
  font-weight: 600;
  color: #323233;
}
.tag-cur {
  font-size: 10px;
  color: #1989fa;
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
  border-radius: 8px;
  padding: 1px 6px;
  line-height: 1.4;
}
.tl-meta {
  margin-top: 3px;
  font-size: 12px;
  color: #969799;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 4px;
}
.tl-opinion {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
  background: #fafafa;
  border-radius: 4px;
  padding: 5px 8px;
  white-space: pre-wrap;
  word-break: break-word;
}

/* 附件链接（比价图片/合同文件/附件 可点开） */
.file-link {
  display: inline-block;
  margin: 2px 8px 2px 0;
  padding: 2px 8px;
  border-radius: 4px;
  background: #ecf5ff;
  color: #1989fa;
  font-size: 13px;
  word-break: break-all;
}
.file-link:active {
  opacity: 0.7;
}
/* 多行文本字段（配置驱动：控件类型=multiline） */
.pre-wrap {
  white-space: pre-wrap;
  word-break: break-word;
}
/* 附件大小（资金审批附件卡片） */
.file-size {
  margin-left: 6px;
  font-size: 12px;
  color: #969799;
}

/* 底部操作栏（审批意见 + 按钮，两行） */
.action-bar {
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100%;
  max-width: 512px;
  background: #fff;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.06);
  padding: 8px 12px;
  padding-bottom: calc(8px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 6px;
  z-index: 10;
}
.action-bar .opinion-field {
  padding: 6px 10px;
  background: #f7f8fa;
  border-radius: 6px;
}
/* 驳回未填意见：输入框标红提示 */
.action-bar .opinion-field.field-error {
  background: #fff5f5;
  border: 1px solid #ee0a24;
}
.action-bar .opinion-field /deep/ .van-field__control {
  font-size: 13px;
  line-height: 1.4;
  max-height: 72px;
}
.action-btns {
  display: flex;
  gap: 12px;
}
.action-bar .van-button {
  flex: 1;
  margin: 0;
}
</style>
